import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button, Tooltip, Menu } from '@amzn/alchemy-components-react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { BreadCrumbs } from 'src/components/breadcrumb';
import { SiteFilter } from 'src/components/site-filter';
import { BulkAction } from 'src/components/bulk-action';
import { RegisterMfaModal } from 'src/components/modals/register-mfa-modal';
import { ManageScan2AuthModal } from 'src/components/modals/manage-scan2auth-modal';
import type { ManageScan2AuthStatusChange } from 'src/components/modals/manage-scan2auth-modal';
import { RemoveScopeModal } from 'src/components/modals/remove-principal-scope-modal';
import { BulkRegisterMfaModal } from 'src/components/modals/bulk-register-mfa-modal';
import { UpdateUserTypeModal } from 'src/components/modals/update-user-type-modal';
import { AddUserModal } from 'src/components/modals/add-user-modal';
import { PrincipalSummaryCard } from 'src/components/cards/principal-summary-card';
import { PrincipalAction } from 'src/components/principal-action';
import { useAuth } from 'src/components/auth/auth-provider';

import {
    DELAY_BETWEEN_USER_BATCH_LOAD_MILLISECONDS,
    BULK_ADD_USER_URL,
    ENTERPRISE_SCOPE,
    NETWORK_ONLY,
    PRINCIPALS_PAGE_SIZE,
    USER_LIST_COMPLETION_MESSAGE_TIMEOUT_IN_MILLISECONDS,
} from 'src/common/constants';
import {AlertType, AuthgardMfaStatus, BaseliningState, IdpType, PageType, TenantID, UserType} from 'src/common/enums';
import { LIST_PRINCIPALS_QUERY } from 'src/common/gql-operations';
import {convertDateTimeToLocaleFormat, getRealm, mfaProvidersForIdpType, mfaStatusForProvider, normalizeIdpType} from 'src/common/util';
import { getScopeMapping } from 'src/components/auth/scope-mapping-provider';
import { getMfaStatuses } from 'src/components/auth/mfa-status-provider';
import { AuthgardResultStatus } from 'src/models/authgard-item-result';
import { convertUnixTimeStampToDate, downloadExcelFile, loadExcelFile } from 'src/common/excel-util';
import { TokenUtil } from 'src/common/token-util';

import { Principal } from 'src/models/principal';
import { PrincipalCard } from 'src/models/principal-card';
import { RegisterMfaModalHooks } from 'src/models/register-mfa-modal-hooks';
import { ManageScan2AuthModalHooks } from 'src/models/manage-scan2auth-modal-hooks';
import { RemoveScopeModalHooks } from 'src/models/remove-principal-scope-modal-hooks';
import { BulkRegisterMfaModalHooks } from 'src/models/bulk-register-mfa-modal-hooks';
import { UpdateUserTypeModalHooks } from 'src/models/update-user-type-modal-hooks';

import { PageProps } from '../page-interface';
import { logger } from 'src/logger';
import * as KatalMetrics from '@amzn/katal-metrics';
import initialMetricsPublisher from 'src/metrics';
import {InplaceSpinner} from "src/components/inplace-spinner";
import {AlertBar} from "src/components/alert-bar";
import {FallbackSpinner} from "src/components/fallback-spinner";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('page', 'principal-list'),
]

// @ts-ignore
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);
const paginationMetricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('pagination', additionalMetricsContext);

// Monotonic sequence for manage-modal MFA write-backs (see PrincipalCard.mfaStatusWriteBackSeq).
// A counter rather than a wall-clock stamp: Date.now() is non-monotonic (NTP steps) and
// millisecond-granular (a write-back landing in the same millisecond a read started would lose
// the tie), and both failure modes silently let a stale read overwrite a fresher value.
let mfaWriteBackSeq = 0;

// True when this card's MFA status was written back by the manage modal after the given sequence
// point — the card then holds fresher, server-confirmed values than the read comparing itself.
const hasFresherWriteBack = (card: PrincipalCard, sinceSeq: number): boolean =>
    card.mfaStatusWriteBackSeq !== undefined && card.mfaStatusWriteBackSeq > sinceSeq;

/**
 * Component to create PrincipalList Page.
 */
export const PrincipalList = (props: PageProps) => {

    const auth = useAuth();
    const { t } = useTranslation();
    const history = useHistory();
    const locale = document.documentElement.lang;   // e.g. en-US

    // TODO: Check with UX on final mockups to see if we should provide baselining / userType filters here

    // TODO: listPrincipals allow 'lastBaselinedBefore' date in input check if we want to provide date filter

    const [getPrincipalScopesLoading, setGetPrincipalScopesLoading] = useState<boolean>(true);
    const [site, setSite] = useState<string>()
    // The IdP type configured for the currently-loaded site (from getScopeMappings), or undefined
    // until it resolves. Drives which MFA-management options each principal card surfaces. Kept in
    // state (not a ref) so the listPrincipals response handler re-runs and builds cards once the
    // idpType is known — which lets the mapping fetch and the principals fetch run in parallel.
    const [siteIdpType, setSiteIdpType] = useState<IdpType>();
    const [scopeOptions, setScopeOptions] = useState<any>([]);
    const [principalCards, setPrincipalCards] = useState<Map<string, PrincipalCard>>(new Map());
    const [principals, setPrincipals] = useState<Principal[]>([]);
    const [downloadButtonAvailable, setDownloadButtonAvailable] = useState(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    const [registerMfaModalOpen, setRegisterMfaModalOpen] = useState<boolean>(false);
    const [registerMfaModalPrincipalArn, setRegisterMfaModalPrincipalArn] = useState<string>();
    const [registerMfaModalFullName, setRegisterMfaModalFullName] = useState<string>();

    const [removeScopeModalOpen, setRemoveScopeModalOpen] = useState<boolean>(false);
    const [removeScopeModalPrincipalArn, setRemoveScopeModalPrincipalArn] = useState<string>();
    const [removeScopeModalFullName, setRemoveScopeModalFullName] = useState<string>();
    const [removeScopeModalTenantId, setRemoveScopeModalTenantId] = useState<string>();
    const [removeScopeModalScope, setRemoveScopeModalScope] = useState<string>();
    const [removeScopeModalUserType, setRemoveScopeModalUserType] = useState<UserType>();

    const [updateUserTypeModalOpen, setUpdateUserTypeModalOpen] = useState<boolean>(false);
    const [updateUserTypeModalPrincipalArn, setUpdateUserTypeModalPrincipalArn] = useState<string>();
    const [updateUserTypeModalFullName, setUpdateUserTypeModalFullName] = useState<string>();
    const [updateUserTypeModalScope, setUpdateUserTypeModalScope] = useState<string>();
    const [updateUserTypeModalTenantId, setUpdateUserTypeModalTenantId] = useState<string>();
    const [updateUserTypeModalUserType, setUpdateUserTypeModalUserType] = useState<string>();

    const [bulkRegisterMfaModalOpen, setBulkRegisterMfaModalOpen] = useState<boolean>(false);
    const [selectedPrincipalCards, setSelectedPrincipalCards] = useState<Map<string, PrincipalCard>>(new Map());

    const [manageScan2AuthModalOpen, setManageScan2AuthModalOpen] = useState<boolean>(false);
    const [manageScan2AuthModalPrincipalArn, setManageScan2AuthModalPrincipalArn] = useState<string>('');
    const [manageScan2AuthModalFullName, setManageScan2AuthModalFullName] = useState<string>('');
    const [manageScan2AuthModalAlias, setManageScan2AuthModalAlias] = useState<string>('');
    const [manageScan2AuthModalTitle, setManageScan2AuthModalTitle] = useState<string>('Manage Scan2Auth');
    const [manageScan2AuthModalInitialStatus, setManageScan2AuthModalInitialStatus] = useState<AuthgardMfaStatus>(AuthgardMfaStatus.NO_RECORD);
    const [manageScan2AuthModalInitialExpiry, setManageScan2AuthModalInitialExpiry] = useState<string | null>(null);
    const [manageScan2AuthModalMfaProvider, setManageScan2AuthModalMfaProvider] = useState<'SCAN2AUTH' | 'FIDO2'>('SCAN2AUTH');
    const [manageScan2AuthModalScope, setManageScan2AuthModalScope] = useState<string>('');
    const [manageScan2AuthModalUserType, setManageScan2AuthModalUserType] = useState<string>('');

    const [addUserModalOpen, setAddUserModalOpen] = useState<boolean>(false);
    const [addUsersIcon, setAddUsersIcon] = useState('caretDown');

    // Auto-loading state
    const [hasMoreToLoad, setHasMoreToLoad] = useState<boolean>(false);
    const [isAutoLoading, setIsAutoLoading] = useState<boolean>(false);
    const [completionMessage, setCompletionMessage] = useState<string | undefined>();
    const [isAliasShown, setIsAliasShown] = useState<boolean>(false);
    // Ref to store scheduled auto-load timeout, allows cancellation on site switch
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Ref to track last processed data object, prevents duplicate processing of same response
    const processedDataRef = useRef<any>(null);
    // Ref to track the site for which data was requested, prevents processing stale data after site change
    const requestedSiteRef = useRef<string | undefined>(undefined);

    const registerMfaModalHooks: RegisterMfaModalHooks = {
        setOpen: setRegisterMfaModalOpen,
        setPrincipalArn: setRegisterMfaModalPrincipalArn,
        setFullName: setRegisterMfaModalFullName
    }

    const removeScopeModalHooks: RemoveScopeModalHooks = {
        setOpen: setRemoveScopeModalOpen,
        setPrincipalArn: setRemoveScopeModalPrincipalArn,
        setFullName: setRemoveScopeModalFullName,
        setTenantId: setRemoveScopeModalTenantId,
        setScope: setRemoveScopeModalScope,
        setUserType: setRemoveScopeModalUserType
    }

    const updateUserTypeModalHooks: UpdateUserTypeModalHooks = {
        setOpen: setUpdateUserTypeModalOpen,
        setPrincipalArn: setUpdateUserTypeModalPrincipalArn,
        setFullName: setUpdateUserTypeModalFullName,
        setTenantId: setUpdateUserTypeModalTenantId,
        setScope: setUpdateUserTypeModalScope,
        setUserType: setUpdateUserTypeModalUserType
    };

    const bulkRegisterMfaModalHooks: BulkRegisterMfaModalHooks = {
        setOpen: setBulkRegisterMfaModalOpen,
    }

    const manageScan2AuthModalHooks: ManageScan2AuthModalHooks = {
        setOpen: setManageScan2AuthModalOpen,
        setPrincipalArn: setManageScan2AuthModalPrincipalArn,
        setFullName: setManageScan2AuthModalFullName,
        setAlias: setManageScan2AuthModalAlias,
        setModalTitle: setManageScan2AuthModalTitle,
        setInitialStatus: setManageScan2AuthModalInitialStatus,
        setInitialExpiry: setManageScan2AuthModalInitialExpiry,
        setMfaProvider: setManageScan2AuthModalMfaProvider,
        setScope: setManageScan2AuthModalScope,
        setUserType: setManageScan2AuthModalUserType,
    }

    // Write-back from the Authgard manage modal: a server-confirmed MFA state change updates the
    // card's seed values, so the card reflects the write and reopening the modal (which re-seeds
    // from these on every open) shows the current state instead of reverting to the pre-write one.
    // Deliberately does NOT touch the modal's own seed state while it is open — that would re-fire
    // the hook's open-effect and wipe a freshly shown registration code. Stamped so a bulk status
    // read already in flight can't overwrite the fresher value when it resolves.
    const handleMfaStatusWriteBack: ManageScan2AuthStatusChange = (principalArn, status, expiryIso) => {
        setPrincipalCards(prev => {
            const card = prev.get(principalArn);
            if (!card) {
                return prev; // the list was reset (site switch) while the operation was in flight
            }
            const updated = new Map(prev);
            updated.set(principalArn, {
                ...card,
                initialMfaStatus: status,
                initialMfaExpiry: expiryIso,
                mfaStatusWriteBackSeq: ++mfaWriteBackSeq,
            });
            return updated;
        });
    };

    const [listPrincipals, { loading: listPrincipalsLoading, error, data }] = useLazyQuery(LIST_PRINCIPALS_QUERY, {
        fetchPolicy: NETWORK_ONLY,
    })

    useEffect(() => {
        props.setActivePage(PageType.PRINCIPAL_LIST);
        populateScopes();
        const realm = getRealm();
        if (realm === 'EUAmazon' || realm === 'USAmazon') {
            setIsAliasShown(true);
        }
    }, []);

    const clearLoadingState = () => {
        // Cancel any pending auto-load to prevent stale data from previous site
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Reset processed data tracker to allow fresh data processing for new site
        processedDataRef.current = null;
        // Clear requested site to invalidate any in-flight requests for the old site
        requestedSiteRef.current = undefined;
        // Clear the resolved IdP type so the new site's cards aren't built until its
        // getScopeMappings result comes back (the response handler gates on this).
        setSiteIdpType(undefined);
        setSelectedPrincipalCards(new Map<string, PrincipalCard>());
        setPrincipals([]);
        setPrincipalCards(new Map<string, PrincipalCard>());
        setHasMoreToLoad(false);
        setIsDownloading(false);
        setErrorMessage(undefined);
        setIsAutoLoading(false);
        setCompletionMessage(undefined);
    }

    // Handle site changes: clear all state and start fresh load for new site
    useEffect(() => {
        if (site) {
            clearLoadingState();
            loadSite(site);
        } else {
            clearLoadingState();
        }
    }, [site]);

    /**
     * Kicks off the site load: fetches the principals and the scope mapping in parallel. The
     * response handler holds off building cards until the mapping resolves the site's idpType
     * (siteIdpType); if the mapping fails, it surfaces an error and no cards are built.
     */
    const loadSite = async (site: string) => {
        // Claim this site as the in-flight target synchronously (before awaiting) so a rapid site
        // switch can be detected when either fetch resolves.
        requestedSiteRef.current = site;
        // Fetch the principals in parallel with the scope mapping. The response handler waits for
        // siteIdpType before building cards, so neither fetch sits behind the other.
        callListPrincipals(site);
        let idpType: IdpType;
        try {
            const mapping = await getScopeMapping(site);
            idpType = normalizeIdpType(mapping.metadata.config.idpType);
        } catch (e) {
            logger.error(`Failed to resolve idpType for site ${site}`, e as Error);
            // Drop if the user switched sites while the mapping was in flight.
            if (requestedSiteRef.current !== site) {
                return;
            }
            // Leave siteIdpType unset so the response handler never builds cards for this site, and
            // surface the error instead of rendering controls that could be wrong for the site.
            setErrorMessage(t('failed-to-load-site-mfa-config'));
            return;
        }
        // Drop the result if the user switched sites while the mapping was in flight.
        if (requestedSiteRef.current !== site) {
            return;
        }
        setSiteIdpType(idpType);
    }

    // Process GraphQL response and trigger auto-loading for paginated results
    useEffect(() => {
        // Skip if loading, no data, data already processed, or data is for a different site (stale response)
        if (!listPrincipalsLoading && data && data !== processedDataRef.current && site && requestedSiteRef.current === site && siteIdpType !== undefined) {
            // Mark this data as processed to prevent re-processing on re-renders
            processedDataRef.current = data;
            setIsAutoLoading(true);
            const fetchedPrincipals: Principal[] = data.listPrincipals.principals;
            logger.info(`Fetched Principals: ${JSON.stringify(fetchedPrincipals)}`);
            const tenantId = data.listPrincipals.tenantId;
            const responseNextToken = data.listPrincipals.nextToken;

            // Update loading state
            setHasMoreToLoad(!!responseNextToken);

            // Append new principals to existing list
            setPrincipals(prev => [...prev, ...fetchedPrincipals]);

            // Resolve the MFA providers for each card from the site's configured IdP type. On an
            // AUTHGARD site this is Scan2Auth for associates / FIDO2 for admins; on MILO or DEFAULT
            // sites it is the existing MILO Register-MFA flow. See mfaProvidersForIdpType.
            const idpType = siteIdpType;
            const batchArns = fetchedPrincipals.map(principal => principal.principalArn);

            setPrincipalCards(prev => {
                const newPrincipalCards = new Map(prev);
                for (const principal of fetchedPrincipals) {
                    newPrincipalCards.set(principal.principalArn, {
                        items: getCardItems(tenantId, principal),
                        expanded: false,
                        fullName: `${principal.firstName} ${principal.lastName}`,
                        alias: principal.alias,
                        scope: principal.scope,
                        userType: principal.userType,
                        selected: false,
                        mfaProviders: mfaProvidersForIdpType(idpType, principal.userType),
                        // initialMfaStatus / initialMfaExpiry are filled in asynchronously below
                        // once bulkGetMfaStatus resolves (AUTHGARD sites only).
                    })
                }
                return newPrincipalCards;
            });

            // Fetch real per-principal MFA status for this batch and merge it into the cards when it
            // resolves. Cards render immediately; the status (which seeds the manage modal) fills in
            // a beat later. AUTHGARD sites only — MILO / DEFAULT sites use the Register-MFA flow,
            // which has no Authgard status to fetch.
            if (idpType === IdpType.AUTHGARD && batchArns.length > 0) {
                // Capture the write-back sequence at dispatch: a card written back by the manage
                // modal after this point holds fresher, server-confirmed values than this read,
                // so the merges below must not overwrite it.
                const enrichmentSeq = mfaWriteBackSeq;
                getMfaStatuses(batchArns).then(resultByArn => {
                    // Discard the result if the user switched sites while the call was in flight.
                    if (requestedSiteRef.current !== site) {
                        return;
                    }
                    setPrincipalCards(prev => {
                        const updated = new Map(prev);
                        for (const arn of batchArns) {
                            const card = updated.get(arn);
                            if (!card) {
                                continue;
                            }
                            if (hasFresherWriteBack(card, enrichmentSeq)) {
                                // The manage modal confirmed a newer status for this card while
                                // this read was in flight — keep the fresher value.
                                continue;
                            }
                            const result = resultByArn.get(arn);
                            if (!result || result.status !== AuthgardResultStatus.SUCCESS) {
                                // Couldn't load this principal's status (failure or unknown) — show
                                // "unavailable" rather than a fabricated "not enrolled".
                                updated.set(arn, { ...card, initialMfaStatus: AuthgardMfaStatus.UNKNOWN, initialMfaExpiry: null });
                                continue;
                            }
                            const { status, expiry } = mfaStatusForProvider(result.value, card.mfaProviders?.[0]);
                            updated.set(arn, { ...card, initialMfaStatus: status, initialMfaExpiry: expiry });
                        }
                        return updated;
                    });
                }).catch(e => {
                    // The whole request failed (network / GraphQL). Mark this batch's cards
                    // "unavailable" so the page shows "couldn't load" instead of a fabricated
                    // "not enrolled". (The >50 guard is unreachable here — batchArns is one page.)
                    logger.error('getMfaStatuses failed while enriching principal cards', e as Error);
                    if (requestedSiteRef.current !== site) {
                        return;
                    }
                    setPrincipalCards(prev => {
                        const updated = new Map(prev);
                        for (const arn of batchArns) {
                            const card = updated.get(arn);
                            if (card && !hasFresherWriteBack(card, enrichmentSeq)) {
                                updated.set(arn, { ...card, initialMfaStatus: AuthgardMfaStatus.UNKNOWN, initialMfaExpiry: null });
                            }
                        }
                        return updated;
                    });
                });
            }

            // Auto-load next batch if more data available
            if (responseNextToken && site) {
                paginationMetricsPublisher.publishCounterMonitor('list-principals-auto-load-request.INVOCATION', 1);
                timeoutRef.current = setTimeout(() => {
                    callListPrincipals(site, responseNextToken);
                }, DELAY_BETWEEN_USER_BATCH_LOAD_MILLISECONDS);
            } else {
                // All data loaded, show completion message
                logger.info(`Principals have been fully loaded for site ${site}`)
                setIsAutoLoading(false);
                setErrorMessage(undefined);
                // Only show "all users loaded" if no custom completion message (e.g., from add-user) is already displayed
                setCompletionMessage(prev => prev || `${t('all-users-loaded')}`);
                graphqlClientMetricsPublisher.publishCounterMonitor('list-principals.SUCCESS', 1);
                setTimeout(() => setCompletionMessage(undefined), USER_LIST_COMPLETION_MESSAGE_TIMEOUT_IN_MILLISECONDS);
            }
        }
        
        // Cleanup timeout on unmount or when effect re-runs
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [data, listPrincipalsLoading, siteIdpType, t])

    useEffect(() => {
        if (error) {
            let message = `${t('list-principals-error-fetching-users')}: ${site}, userTypes:${Object.values(UserType)},
                             baselineStates:${Object.values(BaseliningState)}. ` + error
            setErrorMessage(message)
            logger.error(message)
            graphqlClientMetricsPublisher.publishCounterMonitor('list-principals.ERROR', 1);
        }
    }, [error])

    useEffect(() => {
        allowedToDownloadPrincipals();
        isAliasAvailable();
    }, [principals, scopeOptions]);

    const callListPrincipals = (site: string, token?: string) => {
        // Track which site this request is for to prevent processing stale responses
        requestedSiteRef.current = site;
        
        const listPrincipalsInput = {
            tenantId: TenantID.AFTX,
            scope: site,
            baselineStates: Object.values(BaseliningState),
            userTypes: Object.values(UserType),
            maxResults: PRINCIPALS_PAGE_SIZE,
            nextToken: token
        };
        
        logger.info('Calling listPrincipals.', listPrincipalsInput);
        graphqlClientMetricsPublisher.publishCounterMonitor('list-principals.INVOCATION', 1);
        listPrincipals({
            variables: {
                listPrincipalsInput: listPrincipalsInput
            }
        });
    }

    const handleAddUserSuccess = useCallback((msg: string) => {
        logger.info(`AddUserModal onSuccess called: ${msg}`);
        if (site) {
            // Reload via loadSite (not callListPrincipals directly): clearLoadingState() resets
            // siteIdpType to undefined, and only loadSite re-resolves it. Calling callListPrincipals
            // here would leave the response handler gated on an undefined idpType, so the reloaded
            // list would never render.
            clearLoadingState();
            loadSite(site);
        }
        setCompletionMessage(msg);
    }, [site]);

    const goToBulkAdd = () => {
        logger.info(`Navigating to bulk add users for site: ${site}`);
        const params = new URLSearchParams({ from: 'users' });
        if (site) {
            params.set('scope', site);
        }
        history.push({ pathname: BULK_ADD_USER_URL, search: `?${params.toString()}` });
    };

    const addUsersOptions = [
        { label: t('add-single-user'), action: () => setAddUserModalOpen(true) },
        { label: t('add-multiple-users'), action: goToBulkAdd },
    ];

    /**
     * Populates the filter with the scopes to which the current user has access.
     */
    const populateScopes = async() => {
        const scopes = await auth.getScopes();

        if (scopes && scopes.length > 0) {
            scopes.forEach(scope => scopeOptions.push({
                label: scope,
                value: scope
            }));
        } else {
            setErrorMessage(t('failed-to-get-sites-user-can-access'));
        }

        setScopeOptions(scopeOptions);
        setSite(scopes[0]); // default to first scope to which user has access
        setGetPrincipalScopesLoading(false);
    }

    /**
     * Gets the list of {@link CardItem} from the {@link Principal} object.
     *
     * @param tenantId The id of the tenant.
     * @param principal The principal from which to get the data for the card items.
     */
    const getCardItems = (tenantId: string, principal: Principal) => {
        let cardItems = [
            {
                attributeName: t('user-arn'),
                attributeValue: principal.principalArn,
            },
            {
                attributeName: t('tenant-id'),
                attributeValue: tenantId,
            },
            {
                attributeName: t('user-type'),
                attributeValue: principal.userType,
            },
            {
                attributeName: t('first-name'),
                attributeValue: principal.firstName
            },
            {
                attributeName: t('last-name'),
                attributeValue: principal.lastName
            },
            {
                attributeName: t('site'),
                attributeValue: principal.scope,
            },
            {
                attributeName: t('last-baselining-date'),
                attributeValue: principal.lastBaseliningDate ?
                    convertDateTimeToLocaleFormat(locale, new Date(principal.lastBaseliningDate)) : ''
            },
            {
                attributeName: t('last-baselined-by-principal'),
                attributeValue: principal.lastBaselinedByPrincipal,
            }
        ];

        // Per https://sim.amazon.com/issues/AFTI-3984, display alias information in EU and NA for anyone, in JP only for 1p users
        if (isAliasShown) {
            cardItems.push({
                attributeName: t('system-access-credentials'),
                attributeValue: principal.alias,
            });
        }

        return cardItems;
    }

    /**
     * Creates principal cards react nodes for infinite scrolling items.
     */
    const createPrincipalCardsReactNodes = (principalCards: Map<string, PrincipalCard>) => {
        let nodes: React.ReactNode[] = [];
        principalCards.forEach((principalCard, principalArn) => {
            nodes.push(
                <PrincipalSummaryCard
                    key={principalArn}
                    cardKey={principalArn}
                    selected={principalCard.selected}
                    summaryCardHeader={`${principalCard.fullName} : ${principalCard.userType}`}
                    summaryCardItems={principalCard.items}
                    actionComponent={
                        <PrincipalAction
                            principalArn={principalArn}
                            fullName={principalCard.fullName}
                            alias={principalCard.alias}
                            tenantId={TenantID.AFTX}
                            scope={principalCard.scope}
                            userType={principalCard.userType}
                            className="summary-card-action"
                            mfaProviders={principalCard.mfaProviders || []}
                            initialMfaStatus={principalCard.initialMfaStatus}
                            initialMfaExpiry={principalCard.initialMfaExpiry}
                            registerMfaModalHooks={registerMfaModalHooks}
                            manageScan2AuthModalHooks={manageScan2AuthModalHooks}
                            removeScopeModalHooks={removeScopeModalHooks}
                            updateUserTypeModalHooks={updateUserTypeModalHooks}
                        />
                    }
                    toggleExpand={toggleExpand}
                    toggleSelect={toggleSelect}
                    expanded={principalCard.expanded}
                />
            )
        })

        logger.info(`${nodes.length} cards displayed.`);
        return nodes;
    }

    /**
     * Downloads loaded principals on the page.
     */
    const downloadPrincipals = async () => {
        setIsDownloading(true);

        // Generate Excel with loaded principals
        await generateExcelForPrincipals(principals);

        setIsDownloading(false);
    }

    /**
     * Expands all principal cards.
     *
     * @param principalCards The principal cards to expand.
     */
    const expandAllCards = (principalCards: Map<string, PrincipalCard>) => {
        for (const principalArn of principalCards.keys()) {
            const principalCard = principalCards.get(principalArn)!;
            // Spread, don't enumerate: rebuilding the card field-by-field silently drops any field
            // not on the list (it erased mfaStatusWriteBackSeq, re-arming the stale-read overwrite
            // that stamp exists to prevent) and breaks again for every field added later.
            principalCards.set(principalArn, { ...principalCard, expanded: true });
        }

        setPrincipalCards(new Map(principalCards));
    }

    /**
     * Collapses all principal cards.
     *
     * @param principalCards The principal cards to collapse.
     */
    const collapseAllCards = (principalCards: Map<string, PrincipalCard>) => {
        for (const principalArn of principalCards.keys()) {
            const principalCard = principalCards.get(principalArn)!;
            principalCards.set(principalArn, { ...principalCard, expanded: false });
        }

        setPrincipalCards(new Map(principalCards));
    }

    /**
     * Expands the principal card if it's collapsed or collapses the principal card if it's expanded.
     *
     * @param principalArn The principal arn of the principal card to expand or collapse.
     */
    const toggleExpand = (principalArn: string) => {
        const principalCard = principalCards.get(principalArn)!;
        principalCards.set(principalArn, { ...principalCard, expanded: !principalCard.expanded });

        setPrincipalCards(new Map(principalCards));
    }

    /**
     * Selects the principal card if it's unselected or deselects the principal card if it's selected.
     *
     * @param principalArn The principal arn of the principal card to select or deselect.
     */
    const toggleSelect = (principalArn: string) => {
        const principalCard = principalCards.get(principalArn)!;
        principalCards.set(principalArn, { ...principalCard, selected: !principalCard.selected });
        setPrincipalCards(principalCards);

        const newCard = principalCards.get(principalArn)!;
        const newSelectedPrincipalCards = new Map(selectedPrincipalCards);
        if (newCard.selected) {
            newSelectedPrincipalCards.set(principalArn, newCard);
        } else {
            newSelectedPrincipalCards.delete(principalArn);
        }
        setSelectedPrincipalCards(newSelectedPrincipalCards);
    }


    const allowedToDownloadPrincipals = () => {
        // No principals
        if (principals === undefined || principals.length === 0) {
            logger.info("No principals not showing the Download button")
            setDownloadButtonAvailable(false);
            return;
        }

        logger.info("Display the Download button to all users.");
        setDownloadButtonAvailable(true);
    }


    const isAliasAvailable = () => {
        if (!isAliasShown) {
            // Check for system admin
            const enterpriseScope = scopeOptions.find((s: any) => s.value === ENTERPRISE_SCOPE);
            if (enterpriseScope) {
                logger.info("Show alias on Users page and include alias in downloaded file for System Admins.");
                setIsAliasShown(true);
                return;
            }

            // Determine if the user is an ABBM at the location
            const currentUser = principals.find(principal => principal.principalArn === TokenUtil.getPrincipalArn());
            if (
                currentUser !== undefined
                && [UserType.ABBM_PRIMARY.toString(), UserType.ABBM_SECONDARY.toString()].includes(currentUser.userType)
            ) {
                logger.info("Show alias on Users page and include alias in downloaded file for ABBMs.");
                setIsAliasShown(true);
            }
        }
    }

    /**
     * Generates Excel file for principals.
     */
    const generateExcelForPrincipals = async (principals: Principal[]) => {
        const workbook = await loadExcelFile('users-list.xlsx');

        // Header
        const dataSheet = workbook.worksheets[0];
        let row = dataSheet.getRow(1);
        row.font = {bold: true};
        row.border = {bottom: {style: 'thin'}};
        row.getCell(1).value = t('warehouse') as string;
        row.getCell(2).value = t('first-name') as string;
        row.getCell(3).value = t('last-name') as string;
        row.getCell(4).value = t('creation-date') as string;
        row.getCell(5).value = t('hire-start-date') as string;

        // Per https://sim.amazon.com/issues/AFTI-3984, display alias information in EU and NA for anyone, in JP only for 1p users
        if (isAliasShown) {
            row.getCell(6).value = t('system-access-credentials') as string;
        }

        // Data
        principals.forEach((principal, index) => {
            logger.debug(`principal = ${JSON.stringify(principal)}`);
            row = dataSheet.getRow(index + 2);
            row.getCell(1).value = site;
            row.getCell(2).value = principal.firstName;
            row.getCell(3).value = principal.lastName;
            row.getCell(4).value = convertUnixTimeStampToDate(principal.creationDate);  // Excel handles the date formatting
            row.getCell(5).value = convertUnixTimeStampToDate(principal.startDate);  // Excel handles the date formatting

            // Per https://sim.amazon.com/issues/AFTI-3984, display alias information in EU and NA for anyone, in JP only for 1p users
            if (isAliasShown) {
                row.getCell(6).value = principal.alias;
            }
        });

        const today = new Date().toISOString().substring(0, 10);
        await downloadExcelFile(workbook, `${site}-users-list-${today}.xlsx`)
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <BreadCrumbs breadcrumbItems={[{ tag: t('users'), path: '/principal' }]} />
            </div>
            <div className="row b-background mx-0" data-testid="users">
                <div className="col align-self-center title m-0 py-1">{t('users')}</div>
            </div>
            <div className="row b-background mx-0 align-items-center">
                <div className="col-lg p-1">
                    {(scopeOptions.length > 0) &&
                        <SiteFilter
                            sites={scopeOptions}
                            site={site}
                            setSite={setSite}
                            status={site ? '' : t('required')}
                        />
                    }
                </div>
                <div className="col-lg-auto d-flex p-1">
                    {/* TODO: Optimize and add more functionality to the menu buttons:
                      *  https://sim.amazon.com/issues/AFTI-1461
                      **/}

                    {/* Large screen size icons */}
                    <Menu
                        className="mr-1 dashboard-buttons d-none d-xl-block"
                        id="add-users-menu"
                        options={addUsersOptions}
                        onClose={() => setAddUsersIcon('caretDown')}
                        onOpen={() => setAddUsersIcon('caretUp')}
                    >
                        <span slot="trigger">
                            <Button icon={addUsersIcon} iconPosition="right" disabled={!site} id="add-users">
                                {t('add-users')}
                            </Button>
                        </span>
                    </Menu>
                    <Button
                        className="mr-1 dashboard-buttons d-none d-xl-block"
                        icon="showDetail"
                        iconPosition="right"
                        label={t('expand-all')}
                        onClick={() => expandAllCards(principalCards)}
                        id="expand-all"
                    />
                    <Button
                        className="dashboard-buttons mr-1 d-none d-xl-block"
                        icon="hideDetail"
                        iconPosition="right"
                        label={t('collapse-all')}
                        onClick={() => collapseAllCards(principalCards)}
                        id="collapse-all"
                    />
                    {downloadButtonAvailable && !isAutoLoading &&
                            <Button
                                className="dashboard-buttons mr-1 d-none d-xl-block"
                                icon="download"
                                iconPosition="right"
                                label={isDownloading ? t('downloading') : t('download-page')}
                                onClick={downloadPrincipals}
                                disabled={isDownloading}
                                id="download-page"
                            />
                    }

                    {/* Small Screen size icons */}
                    <Menu
                        className="mr-1 d-lg-block d-xl-none"
                        id="add-users-menu-labelless"
                        options={addUsersOptions}
                        onClose={() => setAddUsersIcon('caretDown')}
                        onOpen={() => setAddUsersIcon('caretUp')}
                    >
                        <span slot="trigger">
                            <Button icon="add" disabled={!site} id="add-users-labelless" />
                        </span>
                    </Menu>
                    <Button
                        className="mr-1 d-lg-block d-xl-none"
                        icon="showDetail"
                        onClick={() => expandAllCards(principalCards)}
                        id="expand-all-labelless"
                    />
                    <Button
                        className="mr-1 d-lg-block d-xl-none"
                        icon="hideDetail"
                        onClick={() => collapseAllCards(principalCards)}
                        id="collapse-all-labelless"
                    />
                    {downloadButtonAvailable && !isAutoLoading && <Tooltip
                        id="download-page-tooltip-labelless"
                        position="top"
                    >
                        <span slot="trigger">
                            <Button
                                className="mr-1 d-lg-block d-xl-none"
                                icon="download"
                                onClick={downloadPrincipals}
                                disabled={isDownloading}
                                id="download-page-labelless"
                            />
                        </span>
                        <div className="tooltip-content">
                            {t('download-page-tooltip-content')}
                        </div>
                    </Tooltip>}

                    {/* Always displayed the same no matter the screen size */}
                    <BulkAction
                        principalArnsCount={selectedPrincipalCards.size}
                        bulkRegisterMfaModalHooks={bulkRegisterMfaModalHooks}
                    />
                </div>
            </div>

            {/* Display alert bar if there's an error */}
            {errorMessage && (
                <AlertBar id="principal-list-alert-bar"
                          result={AlertType.error}
                          dismissible={false}
                          header={t('list-principals-error-alert-header')}
                          message={errorMessage}
                />
            )}

            {/* Display loading spinner for initial load. The site's principals and its idpType
                (getScopeMappings) load in parallel, so keep the spinner up while either is still
                outstanding — including the window where listPrincipals has resolved but siteIdpType
                has not (cards are gated on it) — so the page isn't briefly blank. On a mapping
                failure errorMessage is set instead, so the spinner yields to the error alert. */}
            {(getPrincipalScopesLoading
                || (listPrincipalsLoading && principalCards.size === 0)
                || (!!site && !errorMessage && siteIdpType === undefined && principalCards.size === 0)
              ) && <FallbackSpinner />}

            {/* Auto-loading progress indicator */}
            {isAutoLoading && (
                <div className="row mx-0 my-2">
                    <div className="col text-center">
                        <InplaceSpinner />
                        <div className="mt-2">
                            {t('loading-all-users')}
                        </div>
                    </div>
                </div>
            )}

            {/* Principal cards display */}
            {!getPrincipalScopesLoading && principalCards.size > 0 && (
                <div className="row mx-0">
                    <div className="col">
                        {createPrincipalCardsReactNodes(principalCards)}
                        {!isAutoLoading && !hasMoreToLoad && (
                            <div className="text-center my-3 text-muted">
                                {t('all-users-loaded')} {t('end-of-list')}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <RegisterMfaModal
                open={registerMfaModalOpen}
                setOpen={setRegisterMfaModalOpen}
                principalArn={registerMfaModalPrincipalArn!}
                setPrincipalArn={setRegisterMfaModalPrincipalArn}
                fullName={registerMfaModalFullName!}
                setFullName={setRegisterMfaModalFullName}
            />
            <ManageScan2AuthModal
                open={manageScan2AuthModalOpen}
                setOpen={setManageScan2AuthModalOpen}
                principalArn={manageScan2AuthModalPrincipalArn}
                fullName={manageScan2AuthModalFullName}
                alias={manageScan2AuthModalAlias}
                modalTitle={manageScan2AuthModalTitle}
                initialStatus={manageScan2AuthModalInitialStatus}
                initialExpiry={manageScan2AuthModalInitialExpiry}
                mfaProvider={manageScan2AuthModalMfaProvider}
                scope={manageScan2AuthModalScope}
                userType={manageScan2AuthModalUserType}
                onStatusChange={handleMfaStatusWriteBack}
            />
            <RemoveScopeModal
                open={removeScopeModalOpen}
                setOpen={setRemoveScopeModalOpen}
                principalArn={removeScopeModalPrincipalArn!}
                setPrincipalArn={setRemoveScopeModalPrincipalArn}
                fullName={removeScopeModalFullName!}
                setFullName={setRemoveScopeModalFullName}
                tenantId={removeScopeModalTenantId!}
                setTenantId={setRemoveScopeModalTenantId}
                scope={removeScopeModalScope!}
                setScope={setRemoveScopeModalScope}
                userType={removeScopeModalUserType!}
                setUserType={setRemoveScopeModalUserType}
            />
            <BulkRegisterMfaModal
                open={bulkRegisterMfaModalOpen}
                setOpen={setBulkRegisterMfaModalOpen}
                principalCards={selectedPrincipalCards}
            />
            <UpdateUserTypeModal
                open={updateUserTypeModalOpen}
                setOpen={setUpdateUserTypeModalOpen}
                principalArn={updateUserTypeModalPrincipalArn!}
                setPrincipalArn={setUpdateUserTypeModalPrincipalArn}
                fullName={updateUserTypeModalFullName!}
                setFullName={setUpdateUserTypeModalFullName}
                tenantId={updateUserTypeModalTenantId!}
                setTenantId={setUpdateUserTypeModalTenantId}
                scope={updateUserTypeModalScope!}
                setScope={setUpdateUserTypeModalScope}
                userType={updateUserTypeModalUserType!}
                setUserType={setUpdateUserTypeModalUserType}
            />
            <AddUserModal
                open={addUserModalOpen}
                setOpen={setAddUserModalOpen}
                scope={site || 'Scope'}
                onSuccess={handleAddUserSuccess}
            />

            {completionMessage && (
                <AlertBar
                    id="completion-alert-bar"
                    result={AlertType.success}
                    header={t('success')}
                    message={completionMessage}
                    dismissible={true}
                    onDismiss={() => setCompletionMessage(undefined)}
                />
            )}

        </div>
    )
}
